import { useDisclosure } from "@chakra-ui/hooks";
import { useCallback, useEffect, useState } from "react";
import { getPackages, getTags } from "./api";
import { Package } from "./types";
import PackageDialog from "./components/PackageDialog";
import Search from "./components/Search";
import Loader from "components/Loader";
import { useQueryParams } from "hooks/useQueryParams";
import { TiChevronLeftOutline } from "react-icons/ti";
import { FaChevronCircleRight } from "react-icons/fa";
import PackageCard from "./components/PackageCard";
import { handleAxiosAndCaughtError } from "utils";
import { useTranslation } from "react-i18next";

const MarketplaceView = () => {
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedPackage, setSelectedPackage] = useState<Package | null>(null);
  const [packages, setPackages] = useState<Package[]>([]);
  const { params, updateParams } = useQueryParams();
  const itemsPerPage = 8;
  const [totalPackages, setTotalPackages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [tags, setTags] = useState<{ id: string; name: string }[]>([]);

  const fetchTags = useCallback(async () => {
    try {
      const tagsData = await getTags();
      setTags(tagsData);
    } catch (err) {
      console.error("Failed to fetch tags:", err);
    }
  }, []);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  const fetchPackages = useCallback(
    async (signal: AbortSignal) => {
      try {
        const response = await getPackages({
          q: params.query || undefined,
          tags: selectedTags.length > 0 ? selectedTags.join(",") : undefined,
          page: params.page,
        });
        setPackages(response.items);
        setTotalPackages(response.total);
        setError(null);
      } catch (err) {
        // @ts-expect-error: no prob
        if (err.name === "CanceledError" || signal?.aborted) return;
        setError(t("marketplace.failedToFetch"));
        handleAxiosAndCaughtError(err);
      } finally {
        setLoading(false);
      }
    },
    [params.page, params.query, selectedTags, t]
  );

  useEffect(() => {
    const abortController = new AbortController();
    setLoading(true);
    fetchPackages(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [fetchPackages]);

  const totalPages = Math.ceil(totalPackages / itemsPerPage);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      updateParams({ page: newPage });
    }
  };

  const handleUpdate = useCallback(
    (pkg: Package) => {
      setSelectedPackage(pkg);
      onOpen();
    },
    [onOpen]
  );

  const handleClose = useCallback(() => {
    setSelectedPackage(null);
    onClose();
    const abortController = new AbortController();
    fetchPackages(abortController.signal);
  }, [onClose, fetchPackages]);

  const handleRetry = () => {
    setLoading(true);
    setError(null);
    const abortController = new AbortController();
    fetchPackages(abortController.signal);
  };

  const handleTagClick = (tagName: string) => {
    setSelectedTags((prev) => {
      if (tagName === "") {
        // Click "All Tags" clears selection
        return [];
      }
      // Toggle tag selection
      if (prev.includes(tagName)) {
        return prev.filter((t) => t !== tagName);
      } else {
        return [...prev, tagName];
      }
    });
    updateParams({ page: 1 });
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Loader
          txt={t("marketplace.loadingPackages")}
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
        />
      );
    }

    if (error) {
      return (
        <div className="absolute left-1/2 top-1/2 mt-[38px] flex -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center gap-4">
          <p className="text-lg font-medium text-red-500">{error}</p>
          <button
            onClick={handleRetry}
            className="rounded-lg bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
          >
            {t("common.retry")}
          </button>
        </div>
      );
    }

    if (packages?.length === 0) {
      const emptyMessage =
        params.query || selectedTags.length > 0
          ? t("marketplace.noPackagesFound")
          : t("marketplace.noPackagesAvailable");

      return (
        <div className="absolute left-1/2 top-1/2 mt-[38px] flex -translate-x-1/2 -translate-y-1/2 items-center justify-center">
          <p className="text-lg font-medium text-navy-700 dark:text-white">
            {emptyMessage}
          </p>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 2xl:grid-cols-2 3xl:grid-cols-3">
        {packages?.map((pkg) => (
          <PackageCard key={pkg.id} package={pkg} onEdit={handleUpdate} />
        ))}
      </div>
    );
  };

  return (
    <div className="relative mt-12 h-[75vh] w-full rounded-[20px] px-6 sm:mt-0">
      <div className="mb-5 w-full">
        <Search
          value={params.query}
          onChange={(value) => updateParams({ query: value, page: 1 })}
          onNew={onOpen}
        />
        {tags.length > 0 && (
          <div className="mt-4">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleTagClick("")}
                className={`h-7 rounded-full px-4 py-1 text-sm font-medium transition-colors ${
                  selectedTags.length === 0
                    ? "bg-brand-500 text-white hover:bg-brand-600"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                }`}
              >
                {t("marketplace.allTags")}
              </button>
              {tags.map((tag) => (
                <button
                  key={tag.id}
                  onClick={() => handleTagClick(tag.name)}
                  className={`h-7 rounded-full px-4 py-1 text-sm font-medium transition-colors ${
                    selectedTags.includes(tag.name)
                      ? "bg-brand-500 text-white hover:bg-brand-600"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                  }`}
                >
                  {tag.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {renderContent()}

      {totalPages > 1 && (
        <div className="mt-8 flex items-center justify-center gap-2">
          <button
            onClick={() => handlePageChange(params.page - 1)}
            disabled={params.page === 1}
            className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400"
          >
            <TiChevronLeftOutline />
          </button>

          {[...Array(totalPages)].map((_, index) => (
            <button
              key={index}
              onClick={() => handlePageChange(index + 1)}
              className={`flex h-10 w-10 items-center justify-center rounded-lg border ${
                params.page === index + 1
                  ? "border-blue-500 bg-blue-500 text-white"
                  : "border-gray-200 bg-white text-gray-500 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400"
              }`}
            >
              {index + 1}
            </button>
          ))}

          <button
            onClick={() => handlePageChange(params.page + 1)}
            disabled={params.page === totalPages}
            className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400"
          >
            <FaChevronCircleRight />
          </button>
        </div>
      )}
      <PackageDialog
        isOpen={isOpen}
        onClose={handleClose}
        package={selectedPackage}
      />
    </div>
  );
};

export default MarketplaceView;
